import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { MessageCircle, X, Send, Sparkles, CheckCircle2 } from "lucide-react";

const STORAGE_KEY = "sl_chat_session";
const STORAGE_MSGS = "sl_chat_msgs";
const STORAGE_TEASE_DISMISSED = "sl_chat_tease_dismissed";

// Routes where we should NOT auto-pop the proactive tease bubble.
// Kiosks (customers tapping in-store), wallet pass pages, and billing
// flows shouldn't be interrupted by a sales nudge.
function shouldSuppressTease() {
  if (typeof window === "undefined") return true;
  const p = window.location.pathname || "";
  return (
    p.startsWith("/kiosk") ||
    p.startsWith("/pass/") ||
    p.startsWith("/merchant/billing/")
  );
}

export default function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const [tease, setTease] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", address: "", message: "" });
  const [submittingForm, setSubmittingForm] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      const m = sessionStorage.getItem(STORAGE_MSGS);
      if (s) setSessionId(s);
      if (m) setMessages(JSON.parse(m));
    } catch { /* ignore */ }
  }, []);

  // Proactive tease bubble: after 6 seconds on the page, nudge the visitor.
  // - Only fires once per session (dismissal stored in sessionStorage)
  // - Suppressed on kiosk / wallet-pass / billing routes
  // - Skipped if the chat is already open or a previous chat exists
  useEffect(() => {
    if (shouldSuppressTease()) return;
    let dismissed = false;
    try { dismissed = !!sessionStorage.getItem(STORAGE_TEASE_DISMISSED); } catch { /* ignore */ }
    if (dismissed) return;
    const t = setTimeout(() => {
      if (!open && messages.length === 0) setTease(true);
    }, 6000);
    return () => clearTimeout(t);
  }, [open, messages.length]);

  const dismissTease = () => {
    setTease(false);
    try { sessionStorage.setItem(STORAGE_TEASE_DISMISSED, "1"); } catch { /* ignore */ }
  };

  const openCallback = () => {
    // Skip the AI conversation entirely — visitor wants us to reach out.
    dismissTease();
    setOpen(true);
    setShowForm(true);
    setSubmittedTicket(false);
    setForm((f) => ({ ...f, message: f.message || "Saw your ad — interested in SparkLoop. Please reach out." }));
  };

  const openChat = () => {
    dismissTease();
    setOpen(true);
  };

  useEffect(() => {
    try {
      if (sessionId) sessionStorage.setItem(STORAGE_KEY, sessionId);
      sessionStorage.setItem(STORAGE_MSGS, JSON.stringify(messages.slice(-50)));
    } catch { /* ignore */ }
  }, [sessionId, messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, showForm, submittedTicket]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const { data } = await api.post("/support/chat", {
        session_id: sessionId,
        message: text,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "ai", text: data.reply }]);
      if (data.show_form) {
        setShowForm(true);
        // Pre-fill the form message with a brief auto-summary so the admin has context
        const lastFew = messages.slice(-4).map((m) => `${m.role === "user" ? "Me" : "Us"}: ${m.text}`).join("\n");
        setForm((f) => ({ ...f, message: f.message || lastFew }));
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "ai", text: `Sorry — ${formatApiError(err)}` }]);
    } finally {
      setSending(false);
    }
  };

  const submitTicket = async (e) => {
    e?.preventDefault();
    // Visitor is free to fill in whatever they want — we just need ANY contact
    // signal (name OR email OR phone OR business name) so we have something
    // to follow up on.
    const hasAny = [form.name, form.email, form.phone, form.business_name]
      .some((v) => (v || "").trim().length > 0);
    if (!hasAny) return;
    setSubmittingForm(true);
    try {
      await api.post("/support/ticket", {
        session_id: sessionId,
        ...form,
      });
      setShowForm(false);
      setSubmittedTicket(true);
    } catch (err) {
      setMessages((m) => [...m, { role: "ai", text: `Couldn't submit — ${formatApiError(err)}` }]);
    } finally {
      setSubmittingForm(false);
    }
  };

  return (
    <>
      {!open && tease && (
        <div
          data-testid="support-chat-tease"
          className="fixed bottom-24 right-6 z-40 w-[calc(100vw-3rem)] sm:w-80 bg-white border border-[#e5e5eb] rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-300"
        >
          <button
            data-testid="support-chat-tease-close"
            onClick={dismissTease}
            aria-label="Close"
            className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#5a5a65] hover:bg-[#f9f9fb] flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="p-4 pr-9">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#002fa7]/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#002fa7]" strokeWidth={2.5} />
              </div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#002fa7]">
                SparkLoop team
              </div>
            </div>
            <div className="font-display text-base font-extrabold text-[#0a0a0a] leading-snug mb-1">
              Hi 👋 Need any help?
            </div>
            <p className="text-[13px] text-[#5a5a65] leading-snug mb-3">
              Tell us a bit about you and your business — we'll personally get in touch, usually within an hour.
            </p>
            <div className="flex flex-col gap-2">
              <button
                data-testid="support-chat-tease-callback"
                onClick={openCallback}
                className="w-full h-10 rounded-full bg-[#002fa7] hover:bg-[#001a55] text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5"
              >
                Leave my info
              </button>
              <button
                data-testid="support-chat-tease-chat"
                onClick={openChat}
                className="w-full h-9 rounded-full bg-white border border-[#e5e5eb] hover:bg-[#f9f9fb] text-[#0a0a0a] text-sm font-semibold"
              >
                Just have a question
              </button>
            </div>
          </div>
        </div>
      )}

      {!open && (
        <button
          data-testid="support-chat-launcher"
          onClick={openChat}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#002fa7] hover:bg-[#001a55] text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={2.2} />
          {tease && (
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#ff3355] border-2 border-white animate-pulse" />
          )}
        </button>
      )}

      {open && (
        <div
          data-testid="support-chat-panel"
          className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] sm:w-96 h-[34rem] max-h-[85vh] bg-white border border-[#e5e5eb] rounded-3xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-[#002fa7] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                <Sparkles className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-extrabold text-base leading-tight">SparkLoop help</div>
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Usually replies in minutes</div>
              </div>
            </div>
            <button
              data-testid="support-chat-close"
              onClick={() => setOpen(false)}
              className="opacity-70 hover:opacity-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f9f9fb]">
            {!showForm && !submittedTicket && (
              <button
                data-testid="support-chat-callback-cta"
                onClick={() => {
                  setShowForm(true);
                  setForm((f) => ({
                    ...f,
                    message:
                      f.message ||
                      (messages.length > 0
                        ? messages.slice(-4).map((m) => `${m.role === "user" ? "Me" : "Us"}: ${m.text}`).join("\n")
                        : "Saw your ad — interested in SparkLoop. Please reach out."),
                  }));
                }}
                className="w-full bg-white border-2 border-[#002fa7]/15 hover:border-[#002fa7]/40 rounded-2xl p-3 flex items-center gap-3 text-left transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-[#002fa7] text-white flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-sm text-[#0a0a0a] leading-tight">Want us to reach out?</div>
                  <div className="text-[11px] text-[#5a5a65] leading-tight mt-0.5">Leave your info — we'll get back to you today.</div>
                </div>
              </button>
            )}
            {messages.length === 0 && !showForm && !submittedTicket && (
              <div className="text-center text-sm text-[#5a5a65] py-8">
                <div className="font-display text-base font-bold text-[#0a0a0a] mb-1">Hi there 👋</div>
                <div>Ask us anything — how points work,<br />signing up, billing, anything.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#002fa7] text-white rounded-br-md"
                    : "mr-auto bg-white border border-[#e5e5eb] text-[#0a0a0a] rounded-bl-md"
                }`}
              >
                {m.text}
              </div>
            ))}
            {sending && (
              <div className="mr-auto bg-white border border-[#e5e5eb] rounded-2xl rounded-bl-md px-3.5 py-2.5">
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#5a5a65] animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#5a5a65] animate-bounce" style={{ animationDelay: "0.15s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#5a5a65] animate-bounce" style={{ animationDelay: "0.3s" }} />
                </div>
              </div>
            )}

            {submittedTicket && (
              <div data-testid="chat-ticket-success" className="bg-[#dcf6e2] border border-[#a8dfb8] rounded-2xl p-4 text-center">
                <CheckCircle2 className="w-8 h-8 text-[#1a6b34] mx-auto mb-2" />
                <div className="font-display font-extrabold text-[#1a6b34] mb-1">Got it — thanks!</div>
                <div className="text-sm text-[#1a6b34]/80">
                  Our team will be in touch with you shortly.
                </div>
              </div>
            )}

            {showForm && !submittedTicket && (
              <form
                data-testid="chat-ticket-form"
                onSubmit={submitTicket}
                className="bg-white border border-[#e5e5eb] rounded-2xl p-4 space-y-3"
              >
                <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[#002fa7]">
                  Tell us about you
                </div>
                <div className="text-[11px] text-[#5a5a65] leading-snug -mt-1">
                  Fill in whatever you're comfortable sharing — we'll reach out using any of the info below.
                </div>
                <FormField label="Your name">
                  <input
                    data-testid="ticket-name-input"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-[#e5e5eb] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002fa7]/20 focus:border-[#002fa7]"
                  />
                </FormField>
                <FormField label="Business name">
                  <input
                    data-testid="ticket-biz-input"
                    type="text"
                    value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-[#e5e5eb] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002fa7]/20 focus:border-[#002fa7]"
                  />
                </FormField>
                <FormField label="Business address">
                  <input
                    data-testid="ticket-address-input"
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Street, city, state"
                    className="w-full h-10 px-3 rounded-xl border border-[#e5e5eb] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002fa7]/20 focus:border-[#002fa7]"
                  />
                </FormField>
                <FormField label="Email">
                  <input
                    data-testid="ticket-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-[#e5e5eb] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002fa7]/20 focus:border-[#002fa7]"
                  />
                </FormField>
                <FormField label="Phone">
                  <input
                    data-testid="ticket-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-[#e5e5eb] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002fa7]/20 focus:border-[#002fa7]"
                  />
                </FormField>
                <button
                  data-testid="ticket-submit-btn"
                  type="submit"
                  disabled={
                    submittingForm ||
                    ![form.name, form.email, form.phone, form.business_name].some((v) => (v || "").trim())
                  }
                  className="w-full h-10 rounded-full bg-[#002fa7] hover:bg-[#001a55] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {submittingForm ? "Sending…" : "Send to SparkLoop"}
                </button>
              </form>
            )}
          </div>

          <form onSubmit={send} className="border-t border-[#e5e5eb] bg-white p-3 flex items-center gap-2">
            <input
              data-testid="support-chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={submittedTicket ? "Ask another question…" : "Type your message…"}
              disabled={sending}
              className="flex-1 h-10 px-4 rounded-full bg-[#f5f5f7] border border-[#e5e5eb] text-sm focus:outline-none focus:ring-2 focus:ring-[#002fa7]/20 focus:border-[#002fa7] disabled:opacity-50"
              maxLength={2000}
            />
            <button
              data-testid="support-chat-send"
              type="submit"
              disabled={sending || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#002fa7] hover:bg-[#001a55] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-[#5a5a65] mb-1 block">{label}</span>
      {children}
    </label>
  );
}
